import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { GlobalState } from "../../../../GlobalState";
import { TabTitle } from "../../../../util/titleChanger";
import { ToastContainer, toast } from "react-toastify";
import { createNewInvoice } from "../../../../action"; // Ensure this returns a Promise

function OrderedProductView() {
  TabTitle("Ordered Product View");
  const { orId } = useParams();
  const state = useContext(GlobalState);

  const [allOrders] = state.orderAPI.allOrders;
  const [, setOrderId] = state.orderedProductsAPI.orderId;
  const [orderData, setOrderData] = useState(null);

  const signInUser = JSON.parse(localStorage.getItem("user_data"));

  useEffect(() => {
    const orderId = parseInt(orId);
    setOrderId(orderId);

    const found = allOrders.find((order) => order.id === orderId);
    if (found) {
      setOrderData(found);
    }
  }, [orId, allOrders, setOrderId]);

  const getReceiptStyles = () => `
    @page {
      size: 80mm auto;
      margin: 0;
    }
    body {
      width: 80mm !important;
      font-family: 'Courier New', monospace !important;
      font-size: 12px !important;
      padding: 5mm !important;
      background: white !important;
      color: black !important;
      -webkit-print-color-adjust: exact !important;
    }
    .receipt {
      width: 100%;
    }
    .receipt-header {
      text-align: center;
      font-weight: bold;
      margin-bottom: 10px;
    }
    .divider {
      border-top: 1px dashed #000;
      margin: 5px 0;
    }
    .item-row {
      display: flex;
      justify-content: space-between;
      margin: 3px 0;
    }
    .total {
      font-weight: bold;
      margin-top: 10px;
    }
    .footer {
      text-align: center;
      margin-top: 10px;
    }
  `;

  const generateReceiptHTML = (invoice) => `
    <div class="receipt-header">
      <h2>BUY ME NOW</h2>
      <p>123 Example St, City</p>
    </div>
    <div class="divider"></div>
    <p>Invoice #: ${invoice.invoice_no}</p>
    <p>Date: ${new Date(invoice.date).toLocaleString()}</p>
    <p>Customer: ${invoice.customer?.name || "Walk-in"}</p>
    <div class="divider"></div>
    ${invoice.items
      ?.map(
        (item) => `
        <div class="item-row">
          <span>${item.quantity}x ${item.product.name}</span>
          <span>Rs ${parseFloat(item.total_price).toFixed(2)}</span>
        </div>
      `
      )
      .join("")}
    <div class="divider"></div>
    <div class="item-row total">
      <span>TOTAL:</span>
      <span>Rs ${parseFloat(invoice.net_price).toFixed(2)}</span>
    </div>
    <div class="footer">Thank you!</div>
  `;

  const handlePrint = () => {
    if (!orderData) {
      toast.error("Order data not loaded");
      return;
    }

    // Prepare payload
    const payload = {
      orderId: orderData.id,
      products: orderData.items.map((item) => ({
        id: item.product.id,
        quantity: item.qty,
        unit_price: item.unit_price,
        total_price: item.qty * item.unit_price,
        discount: item.discount || 0,
      })),
      total_price: orderData.total_amount,
      total_discount: orderData.total_discount || 0,
      net_price: orderData.net_price || orderData.total_amount,
      createdBy: signInUser?.id || 1,
    };

    // Show loading state
    toast.info("Generating invoice...");

    createNewInvoice(payload, (response) => {
      if (!response || !response.invoiceData) {
        toast.error("Invoice creation failed");
        return;
      }

      const invoice = response.invoiceData;

      // Transform the data to match what generateReceiptHTML expects
      const formattedInvoice = {
        invoice_no: invoice.invoice_no,
        date: invoice.created_at,
        customer: {
          name: orderData.customer?.name || "Walk-in",
        },
        items: orderData.items.map((item) => ({
          product: {
            name: item.product?.name,
          },
          quantity: item.qty,
          total_price: item.qty * item.unit_price,
          unit_price: item.unit_price,
        })),
        net_price: invoice.net_price,
      };

      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice #${formattedInvoice.invoice_no}</title>
            <style>${getReceiptStyles()}</style>
          </head>
          <body>
            <div class="receipt">
              ${generateReceiptHTML(formattedInvoice)}
            </div>
            <script>
              window.onload = () => {
                setTimeout(() => {
                  window.print();
                  window.onafterprint = () => window.close();
                }, 300);
              };
            </script>
          </body>
        </html>
      `;

      // Open print window
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        toast.error("Popup blocked. Please allow popups for this site.");
        return;
      }

      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      toast.success("Invoice ready to print!");
    });
  };

  if (!orderData) return <div>Loading...</div>;

  return (
    <>
      <div className="main-container-purchase">
        <h1>Invoice #{orderData.id}</h1>
        <h3>{new Date(orderData.date).toLocaleString()}</h3>

        <div>
          <div className="left-main-container">
            <div className="quantity">
              Customer: <span>{orderData.customer?.name}</span>
            </div>
            <div className="supplier">
              Total Amount: <span>Rs. {orderData.total_amount}</span>
            </div>
            <div className="total">
              Total Qty: <span>{orderData.total_qty}</span>
            </div>
            <div className="description">
              Status: <span>{orderData.status}</span>
            </div>
          </div>

          <div className="right-main-container">
            <table>
              <thead>
                <tr>
                  <th>Product Name</th>
                  <th>Unit Price</th>
                  <th>Qty</th>
                  <th>Total Price</th>
                </tr>
              </thead>
              <tbody>
                {orderData.items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.product?.name}</td>
                    <td>Rs. {item.unit_price}</td>
                    <td>{item.qty}</td>
                    <td>Rs. {(item.qty * parseFloat(item.unit_price)).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div
          style={{
            marginTop: "2rem",
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            style={{
              padding: "12px",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontFamily: "'Poppins', sans-serif",
              backgroundColor: "var(--secondary-color)",
              transition: "background-color 0.4s ease, color 0.4s ease",
              color: "#fff",
            }}
            onClick={handlePrint}
          >
            Print
          </button>
        </div>
      </div>
      <ToastContainer />
    </>
  );
}

export default OrderedProductView;
